import classnames from 'classnames';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  format,
  getDay,
  getDaysInMonth,
  isBefore,
  isPast,
  isSameDay,
  isSameMonth,
  isSameYear,
  isToday,
  Locale,
  setDate,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
  subYears
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import React, { ChangeEvent, FunctionComponent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { useLayer } from 'react-laag';
import { ResizeObserver } from '@juggle/resize-observer';

import { CloseButton, TertiaryButton, TertiaryIconButton } from '@components/library/button';
import {
  BaseDatePickerProps,
  chunk,
  datePickerInputFormats,
  datePickerNumberOfDays,
  DatePickerColumn,
  DatePickerInputFormat,
  DatePickerPaginationTypes,
  getDate,
  getInputFormatLocale,
  validateDate
} from '@components/library/date-picker';
import { FontIcons } from '@components/library/icon';
import { StandardTextField } from '@components/library/text-field';
import { InputType, LabelPosition } from '@components/library/text-field/TextFieldTypes';
import { Placements } from '@components/library/utils';

const baseName = 'date-picker';

export const BaseDatePicker: FunctionComponent<BaseDatePickerProps> = (props) => {
  const {
    actionsProps,
    allowPastDates = true,
    alwaysShowCalendar = false,
    alwaysShowInput = true,
    calendarProps,
    calendarIconButtonProps,
    closeOnClickAway = false,
    closeOnSelection = true,
    datePickerProps,
    defaultOpen = false,
    defaultSelectedDate,
    dialogProps,
    disabled,
    error: userDefinedError,
    gridCellProps,
    gridColumnHeaderProps,
    headerProps,
    gridProps,
    id,
    inputWrapperProps,
    invalidDateError,
    labelCalendarIconButton,
    labelCloseButton,
    labelNextMonthButton,
    labelNextYearButton,
    labelPreviousMonthButton,
    labelPreviousYearButton,
    labelTextfield,
    labelToday,
    locale = navigator.language || '',
    onClose,
    onOpen,
    onSelection,
    placement = Placements.BottomStart,
    previouslySelectedDate,
    showLabel = false,
    textfieldProps,
    todayProps,
    variantName
  } = props;

  // constants for things like id's
  const dialogId = `${id}--dialog`;
  const headingId = `${id}--heading`;
  const inputId = `${id}--input`;
  const calendarIconButtonId = `${id}--calendar-icon-button`;

  const previousSelectedDate = getDate(previouslySelectedDate);
  const now = new Date();

  const [calendar, setCalendar] = useState<Date[][] | null>(null);
  const [columns, setColumns] = useState<DatePickerColumn[]>([]);
  // currentDate is the highlighted date
  const [currentDate, setCurrentDate] = useState(now);
  const [currentLocale, setCurrentLocale] = useState<Locale>(enUS);
  const [error, setError] = useState('');
  const [gridCellFocused, setGridCellFocused] = useState(false);
  const [inputFormat, setInputFormat] = useState<DatePickerInputFormat>(datePickerInputFormats[1]);
  const [localized, setLocalized] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(defaultOpen || false);
  // we need to track if the calendar has been opened at least once
  // so we don't overrite selectedDate with defaultSelectedDate on reopens
  const [opened, setOpened] = useState(defaultOpen || false);
  // selectedDate is the chosen date
  const [selectedDate, setSelectedDate] = useState(now);
  // the date shown in the input
  const [value, setValue] = useState('');

  const calendarIconButtonRef = useRef<HTMLButtonElement | null>(null);
  const currentDateRef = useRef<HTMLTableDataCellElement | null>(null);

  const handleClose = (): void => {
    setOpen(false);

    // when closing, the focus needs to return to the calendar icon button
    if (calendarIconButtonRef && calendarIconButtonRef.current) {
      // this is a little hacky but required
      // focusing on this ref has a timing conflict with onKeyDown below
      // causing the calendar to reopen after being closed on selection
      // so we need a little delay before focusing
      setTimeout(() => {
        calendarIconButtonRef.current.focus();
      }, 100);
    }

    if (onClose) {
      onClose();
    }
  };

  const handleOpen = (): void => {
    setOpen(true);
    setOpened(true);

    if (onOpen) {
      onOpen();
    }
  };

  const generateColumns = (): DatePickerColumn[] => {
    const start = startOfWeek(now);

    return Array(datePickerNumberOfDays).fill(null).map((_, i) => ({
      abbr: format(addDays(start, i), 'EEEEEE', { locale: currentLocale }),
      title: format(addDays(start, i), 'EEEE', { locale: currentLocale })
    }));
  };

  const handleNextDay = (): void => {
    const nextDay = addDays(currentDate, 1);
    setCurrentDate(nextDay);
  };

  const handleNextMonth = (): void => {
    const nextMonth = addMonths(currentDate, 1);
    setCurrentDate(startOfMonth(nextMonth));
  };

  const handleNextWeek = (): void => {
    const nextWeek = addWeeks(currentDate, 1);
    setCurrentDate(nextWeek);
  };

  const handleNextYear = (): void => {
    const nextYear = addYears(currentDate, 1);
    setCurrentDate(startOfMonth(nextYear));
  };

  const handlePreviousDate = (date: Date, startOf?: DatePickerPaginationTypes): Date => {
    // if allowPastDates is set to true, don't run the code below
    if (allowPastDates) return date;
    // today is always allowed
    if (isToday(date)) return date;
    // if we're trying to go past today, just return today
    // example: today is June 15, currentDate is set to July 1, we're trying to click previous month
    if (isPast(date)) return now;
    // else we're just paging by day/week/month/year
    return startOf === 'month' ? startOfMonth(date) : date;
  };

  const handlePreviousDay = (): void => {
    const previousDay = subDays(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousDay));
  };

  const handlePreviousMonth = (): void => {
    const previousMonth = subMonths(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousMonth, 'month'));
  };

  const handlePreviousYear = (): void => {
    const previousYear = subYears(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousYear, 'month'));
  };

  const handlePreviousWeek = (): void => {
    const previousWeek = subWeeks(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousWeek));
  };

  const handleToday = (): void => {
    setCurrentDate(now);
  };

  const handleDateNextMonth = (): void => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateNextYear = (): void => {
    setCurrentDate(addYears(currentDate, 1));
  };

  const handleDatePreviousMonth = (): void => {
    const previousMonth = subMonths(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousMonth));
  };

  const handleDatePreviousYear = (): void => {
    const previousYear = subYears(currentDate, 1);
    setCurrentDate(handlePreviousDate(previousYear));
  };

  const handleMonthStart = (): void => {
    setCurrentDate(handlePreviousDate(startOfMonth(currentDate)));
  };

  const handleMonthEnd = (): void => {
    setCurrentDate(endOfMonth(currentDate));
  };

  const handleDate = (
    pickedDate?: Date,
    inputDate?: string,
    callback?: (date: Date, isoDate: string, e?: React.ChangeEvent<HTMLInputElement>) => void,
    e?: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const { date, dateIso, dateValue, valid } = validateDate(inputFormat, pickedDate, inputDate);

    if (valid) {
      setCurrentDate(date);
      setError('');
      setSelectedDate(date);
      setValue(dateValue);

      if (callback) {
        callback(date, dateIso, e);
      }
    } else {
      setError(invalidDateError);
    }
  };

  const handleDefaultSelectedDate = (): void => {
    if (defaultSelectedDate) {
      if (typeof defaultSelectedDate === 'string') {
        handleDate(undefined, defaultSelectedDate);
      } else {
        handleDate(defaultSelectedDate);
      }
    }
  };

  const handleSelectedDate = (date: Date): void => {
    handleDate(date, undefined, onSelection);

    if (!alwaysShowCalendar && closeOnSelection) {
      handleClose();
    }
  };

  const handleInputDate = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value) {
      handleDate(undefined, e.target.value, onSelection, e);
    } else {
      // user cleared their typed date, reset error
      setError('');
    }
  };

  const generateCalendar = (): Date[][] => {
    // 27, 28, 30, or 31
    const daysInMonth = getDaysInMonth(currentDate);

    const startDateOfCurrentMonth = startOfMonth(currentDate);
    const startWeekday = getDay(startDateOfCurrentMonth);

    const endDateOfCurrentMonth = endOfMonth(currentDate);
    const endWeekday = getDay(endDateOfCurrentMonth);

    const gridDays = chunk([
      // show days before the beginning of the current month, like 1, 2, 3, 4, 5, 6
      ...Array.from({ length: startWeekday }, (_, i) => subDays(startDateOfCurrentMonth, i + 1)).reverse(),
      ...Array.from({ length: daysInMonth }, (_, i) => setDate(currentDate, i + 1)),
      // show days after the end of the current month, like 26, 27, 28, 29, 30, 31
      ...Array.from({ length: 6 - endWeekday }, (_, i) => addDays(endDateOfCurrentMonth, i + 1))
    ], datePickerNumberOfDays);

    return gridDays;
  };

  /**
   * IMPORTANT: This HAS to be handled on KeyDown.
   */
  const handleSelectedDateKeyDown = (e: KeyboardEvent<HTMLTableDataCellElement>): void => {
    const { key, shiftKey } = e;

    switch (key) {
      case 'Enter':
        handleSelectedDate(currentDate);
        break;
      case 'Escape':
        handleClose();
        break;
      case ' ':
        handleSelectedDate(currentDate);
        break;
      case 'PageUp':
        // eslint-disable-next-line no-unused-expressions
        if (shiftKey) {
          handleDatePreviousYear();
        } else {
          handleDatePreviousMonth();
        }
        break;
      case 'PageDown':
        // eslint-disable-next-line no-unused-expressions
        if (shiftKey) {
          handleDateNextYear();
        } else {
          handleDateNextMonth();
        }
        break;
      case 'End':
        handleMonthEnd();
        break;
      case 'Home':
        handleMonthStart();
        break;
      case 'ArrowLeft':
        handlePreviousDay();
        break;
      case 'ArrowUp':
        handlePreviousWeek();
        break;
      case 'ArrowRight':
        handleNextDay();
        break;
      case 'ArrowDown':
        handleNextWeek();
        break;
      default:
        break;
    }
  };

  const handleButtonsKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    const { key } = e;

    switch (key) {
      case 'Escape':
        handleClose();
        break;
      default:
        break;
    }
  };

  const handleCurrentDateFocus = (): void => {
    if (open && currentDateRef && currentDateRef.current) {
      currentDateRef.current.focus();
    }
  };

  const handleLocale = (): void => {
    const { dateFnsLocale, datePickerInputFormat } = getInputFormatLocale(locale);
    setCurrentLocale(dateFnsLocale);
    setInputFormat(datePickerInputFormat);
    setLocalized(true);
  };

  useEffect(() => {
    // 1. when initially mounting, we need to handle the locale for display in the input
    handleLocale();
    setMounted(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // 2. if there's a defaultSelectedDate, handle it
    if (localized && mounted) {
      handleDefaultSelectedDate();
    }
  }, [defaultSelectedDate, localized, mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      // once the calendar is opened, we can run code needed to render it
      setCalendar(generateCalendar());
      setColumns(generateColumns());
      // this should only run once on the first open
      if (!opened) {
        handleDefaultSelectedDate();
      }
      // when the calendar opens, focus on the currentDate (first open will be same as selectedDate)
      handleCurrentDateFocus();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localized && mounted) {
      // updates the currentLocale when the external locale changes
      handleLocale();
    }
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localized && mounted) {
      // the calendar displays different between locales
      setCalendar(generateCalendar());
      setColumns(generateColumns());
    }
  }, [currentLocale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (localized && mounted) {
      // these need to rerun when going from 1st => 30th/31st
      setCalendar(generateCalendar());
      setColumns(generateColumns());

      if (gridCellFocused) {
        handleCurrentDateFocus();
      }
    }
  }, [currentDate, previouslySelectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // when the calendar updates after going from 1st => 30th/31st
    // while navigating via keyboard in the calendar grid
    // we need to refocus back on the currentDate
    // otherwise focus is lost
    if (gridCellFocused && open) {
      handleCurrentDateFocus();
    }
  }, [calendar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOpen(alwaysShowCalendar || defaultOpen || false);
    setOpened(alwaysShowCalendar || defaultOpen || false);
  }, [alwaysShowCalendar, defaultOpen]);

  const previousMonthDisabled = (!allowPastDates && isSameMonth(now, currentDate));
  const previousYearDisabled = (!allowPastDates && isSameYear(now, currentDate));

  const { triggerProps, layerProps, renderLayer } = useLayer({
    auto: true,
    isOpen: !alwaysShowCalendar && open,
    onOutsideClick: closeOnClickAway && handleClose,
    overflowContainer: false,
    placement,
    possiblePlacements: [
      Placements.BottomEnd,
      Placements.BottomStart,
      Placements.RightStart,
      Placements.TopEnd,
      Placements.TopStart
    ],
    ResizeObserver, // eslint-disable-line @typescript-eslint/naming-convention
    triggerOffset: 15
  });

  const renderCalendar = (): ReactNode => (
    <div {...calendarProps}>
      <div {...headerProps}>
        <h6
          aria-live="polite"
          id={headingId}
        >
          {format(currentDate, 'MMMM yyyy', { locale: currentLocale })}
        </h6>
        {!alwaysShowCalendar && (
          <CloseButton
            onClick={(): void => handleClose()}
            onKeyDown={(e): void => handleButtonsKeyDown(e)}
            title={labelCloseButton}
          />
        )}
      </div>
      <div {...actionsProps}>
        <TertiaryIconButton
          disabled={previousYearDisabled}
          icon={FontIcons.CaretLeftDouble}
          onClick={handlePreviousYear}
          onKeyDown={(e): void => handleButtonsKeyDown(e)}
          title={labelPreviousYearButton}
        />
        <TertiaryIconButton
          disabled={previousMonthDisabled}
          icon={FontIcons.CaretLeft}
          onClick={handlePreviousMonth}
          onKeyDown={(e): void => handleButtonsKeyDown(e)}
          title={labelPreviousMonthButton}
        />
        <TertiaryButton
          onClick={handleToday}
          onKeyDown={(e): void => handleButtonsKeyDown(e)}
        >
          {labelToday}
        </TertiaryButton>
        <TertiaryIconButton
          icon={FontIcons.CaretRight}
          onClick={handleNextMonth}
          onKeyDown={(e): void => handleButtonsKeyDown(e)}
          title={labelNextMonthButton}
        />
        <TertiaryIconButton
          icon={FontIcons.CaretRightDouble}
          onClick={handleNextYear}
          onKeyDown={(e): void => handleButtonsKeyDown(e)}
          title={labelNextYearButton}
        />
      </div>
      <table
        {...gridProps}
        aria-labelledby={headingId}
        role="grid"
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                {...gridColumnHeaderProps}
                key={column.abbr}
                abbr={column.title}
                role="columnheader"
              >
                {column.abbr}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calendar && calendar.map((week, weekIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`${id}--week-${weekIndex}`} role="row">
              {week.map((day, dayIndex) => {
                const today = isToday(day);

                const isBeforeToday = isBefore(day, now);
                const isBeforePreviousDate = isBefore(previousSelectedDate, day);
                const isSameDayAsPreviousDate = isSameDay(previousSelectedDate, day);
                const isPreviousDate = !allowPastDates && previousSelectedDate && isBeforeToday && !isBeforePreviousDate && !isSameDayAsPreviousDate && !today;
                const isPastDate = !allowPastDates && !previousSelectedDate && isPast(day) && !today;
                const isDisabled = isPreviousDate || isPastDate || !isSameMonth(day, currentDate);

                const isCurrentDate = isSameDay(currentDate, day);
                const isSelectedDate = isSameDay(selectedDate, day);

                return (
                  <td
                    {...gridCellProps}
                      // eslint-disable-next-line react/no-array-index-key
                    key={`${id}--day-${dayIndex}`}
                      // eslint-disable-next-line no-nested-ternary
                    ref={isCurrentDate ? currentDateRef : undefined}
                    aria-disabled={isDisabled}
                    aria-selected={isSelectedDate}
                    onBlur={(): void => setGridCellFocused(false)}
                    onClick={!isDisabled ? (): void => handleSelectedDate(day) : undefined}
                    onFocus={(): void => setGridCellFocused(true)}
                    onKeyDown={!isDisabled ? (e): void => handleSelectedDateKeyDown(e) : undefined}
                    role="gridcell"
                    tabIndex={isCurrentDate ? 0 : -1}
                    title={format(day, 'PPPP', { locale: currentLocale })}
                  >
                    {format(day, 'd', { locale: currentLocale })}
                    {today && <span {...todayProps} aria-hidden />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div
      {...datePickerProps}
      className={classnames(
        `bf-${baseName}__${variantName}`,
        datePickerProps?.className
      )}
    >
      {alwaysShowInput && (
        <div
          {...triggerProps}
          {...inputWrapperProps}
        >
          <StandardTextField
            {...textfieldProps}
            disabled={disabled}
            error={userDefinedError || error}
            id={inputId}
            label={labelTextfield}
            labelPosition={LabelPosition.Top}
            multiLine={false}
            onBlur={handleInputDate}
            onChange={(e): void => {
              setValue(e.target.value);
              if (textfieldProps && textfieldProps.onChange) {
                textfieldProps.onChange(e);
              }
            }}
            placeholder={inputFormat.placeholder}
            showLabel={showLabel}
            type={InputType.Text}
            value={value}
          />
          {!alwaysShowCalendar && (
            <TertiaryIconButton
              {...calendarIconButtonProps}
              ref={calendarIconButtonRef}
              aria-label={`${labelCalendarIconButton} ${format(selectedDate, 'PPPP', { locale: currentLocale })}`}
              disabled={disabled}
              icon={FontIcons.Calendar}
              id={calendarIconButtonId}
              onClick={(): void => (open ? handleClose() : handleOpen())}
            />
          )}
        </div>
      )}
      {open && alwaysShowCalendar && renderCalendar()}
      {open && !alwaysShowCalendar && renderLayer(
        <div
          {...layerProps}
          {...dialogProps}
          aria-labelledby={headingId}
          aria-modal
          id={dialogId}
          role="dialog"
        >
          <FocusLock
            autoFocus={false}
            crossFrame={false}
          >
            {renderCalendar()}
          </FocusLock>
        </div>
      )}
    </div>
  );
};
